import {
  Button,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ResponseOf } from "../../../core/api";
import useApi from "../../../shared/hooks/useApi";
import { PatientId, PatientServicePlanId } from "../../../shared/schema/schema";
import { formatErrorResponse } from "../../../shared/utils/format-response-error";

type ServicePlan = ResponseOf<"get", "./service_plans/:patientServicePlanId">;

interface Props {
  isLastStep: boolean;
  patientId: PatientId;
  plan: ServicePlan;
  servicePlanId: PatientServicePlanId;
}

/**
 * Finalizing locks the plan and renders its PDF. Drafts get the confirm-and-
 * finalize flow on the last step; finalized plans get a link to the PDF.
 */
export default function ServicePlanFinalizeAction({
  isLastStep,
  patientId,
  plan,
  servicePlanId,
}: Props) {
  const { queries, mutations } = useApi();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const finalizeMutation = useMutation({
    ...mutations.servicePlan.finalize(servicePlanId),
    onSuccess: ({ signedUrl }) => {
      queryClient.invalidateQueries({ queryKey: queries.servicePlan.byId(servicePlanId).queryKey });
      queryClient.invalidateQueries({ queryKey: queries.servicePlan.list(patientId).queryKey });
      onClose();
      // Generating the PDF outlives the browser's user-activation window, so a
      // window.open here would be treated as an unsolicited popup. The toast
      // offers a real link instead, and "View PDF" stays in the footer.
      toast({
        title: "Service plan finalized",
        description: (
          <Link isExternal href={signedUrl} textDecoration="underline">
            Open the PDF
          </Link>
        ),
        status: "success",
        position: "top-right",
        duration: 12000,
        isClosable: true,
      });
    },
    onError: (error: unknown) => {
      toast({
        title: "Could not finalize",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
        isClosable: true,
      });
    },
  });

  const pdfUrlMutation = useMutation({
    ...mutations.servicePlan.getPdfUrl(),
    onSuccess: ({ signedUrl }) => {
      if (signedUrl === null) {
        toast({
          title: "No PDF for this plan",
          status: "warning",
          position: "top-right",
        });
        return;
      }
      window.open(signedUrl, "_blank");
    },
    onError: (error: unknown) => {
      toast({
        title: "Could not open the PDF",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
      });
    },
  });

  if (plan.status === "FINALIZED") {
    return (
      <Button
        isDisabled={!plan.hasPdf}
        isLoading={pdfUrlMutation.isPending}
        marginRight={3}
        onClick={() => pdfUrlMutation.mutate(servicePlanId)}
      >
        View PDF
      </Button>
    );
  }

  const missingSignatures = [
    plan.assessor.signatureData === null ? "assessor" : null,
    plan.consumer.signatureData === null ? "consumer" : null,
  ].filter((role): role is string => role !== null);

  return (
    <>
      <Button
        colorScheme="green"
        isDisabled={!isLastStep || missingSignatures.length > 0}
        marginRight={3}
        title={
          missingSignatures.length > 0
            ? `Missing signatures: ${missingSignatures.join(", ")}`
            : undefined
        }
        onClick={onOpen}
      >
        Finalize service plan
      </Button>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Finalize service plan</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <Text>
                Finalizing locks this service plan and generates its PDF. It cannot be edited
                afterwards.
              </Text>
              <Text color="gray.600" fontSize="sm">
                Diagnosis codes and medications stay editable on the patient&apos;s Medical tab, but
                the PDF keeps the values as they are now.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="green"
              isLoading={finalizeMutation.isPending}
              loadingText="Finalizing..."
              onClick={() => finalizeMutation.mutate(undefined)}
            >
              Finalize and generate PDF
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
